import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { LayoutGrid, UtensilsCrossed, Armchair, Users, ClipboardList, BarChart3, Settings, LogOut, ChefHat, Headphones, Lock, Menu, X, MapPin } from 'lucide-react';
import { useRealtime } from '../lib/useRealtime';
import { useToast } from '../lib/Toast';
import { Logo } from '../components/Logo';
import { LocationSwitcher } from '../components/LocationSwitcher';
import { WaiterAlertPanel } from '../lib/WaiterAlertPanel';
import { hasPlanFeature } from '../lib/plans';
import { roleLabel } from '../lib/roleLabels';
import { isPlatformPanelUser } from '../lib/platformRoles';
import { playOrderNew, autoGrantConsentOnFirstInteraction } from '../lib/notificationSounds';
import { SoundConsentBanner } from '../components/SoundConsentBanner';

// Main operational links — staff use these all day, every day.
const adminLinks = [
    { to: '/r', label: 'Dashboard', icon: LayoutGrid, end: true },
    { to: '/r/menu', label: 'Meniu', icon: UtensilsCrossed },
    { to: '/r/tables', label: 'Mese & QR', icon: Armchair, feature: 'table_management' },
    { to: '/r/staff', label: 'Echipă', icon: Users },
    { to: '/r/orders', label: 'Comenzi', icon: ClipboardList, feature: 'orders_realtime' },
    { to: '/r/kitchen', label: 'Bucătărie (KDS)', icon: ChefHat, feature: 'kitchen_kds' },
    { to: '/r/waiter', label: 'Vedere Ospătar', icon: Headphones, feature: 'waiter_view' },
    { to: '/r/stats', label: 'Statistici', icon: BarChart3, feature: 'stats' },
];
// Account/settings — separated below, less frequent use.
const accountLink = { to: '/r/settings', label: 'Setări cont', icon: Settings };
// Owner-only link (chain locations management).
const locationsLink = { to: '/r/locations', label: 'Locații', icon: MapPin };

export default function RestaurantLayout() {
    const { user, restaurant, logout, impersonating, exitRestaurant } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const { push } = useToast();
    const [mobileOpen, setMobileOpen] = useState(false);
    const closeMobile = () => setMobileOpen(false);

    // Close the drawer on route change.
    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    // Browsers block AudioContext until a user gesture. Instead of nagging
    // the user with a banner, we silently grant sound consent on the FIRST
    // interaction anywhere on the page (click, keypress, touch).
    useEffect(() => { autoGrantConsentOnFirstInteraction(); }, []);

    useRealtime(user?.restaurant_id, (msg) => {
        if (msg.type === 'new_order') {
            push({ title: 'Comandă nouă', message: `Masa ${msg.order.table_number} • ${msg.order.items.length} produse`, variant: 'teal' });
            playOrderNew();
        } else if (msg.type === 'order_updated') {
            // light info update
        }
    });

    // Subscription gate: when status isn't active/trial or the expiry has passed,
    // every nav item shows a lock and clicking it bounces to the Plan tab instead
    // of letting the backend 402 us. `null` restaurant (still loading) = don't
    // lock yet, to avoid a flash of locks on first paint.
    const subActive = (() => {
        if (!restaurant) return true;
        if (!['active', 'trial'].includes(restaurant.status)) return false;
        const exp = restaurant.subscription_expires_at;
        if (!exp) return true;
        return new Date(exp) > new Date();
    })();

    // Owner-ul vede toate funcțiile; cele Pro apar cu lacăt pe planul Standard.
    // Când abonamentul e inactiv, tot sidebar-ul e blocat (vizual + click).
    let links = adminLinks.map((l) => {
        const planLocked = Boolean(l.feature) && !hasPlanFeature(restaurant, l.feature);
        const subLocked = !subActive;
        return {
            ...l,
            locked: planLocked || subLocked,
            lockReason: subLocked ? 'subscription' : (planLocked ? 'feature' : null),
        };
    });
    if (user?.role === 'waiter') links = [links[0], links[6], links[4]];
    else if (user?.role === 'kitchen') links = [links[5]];
    // Only the restaurant owner / managers see Account/Settings.
    const showAccount = ['restaurant_admin', 'manager'].includes(user?.role) || isPlatformPanelUser(user?.role);

    const handleLogout = async () => { closeMobile(); await logout(); nav('/login', { replace: true, state: {} }); };

    const backToSuperAdmin = async () => {
        try {
            await exitRestaurant(() => nav('/admin'));
        } catch (err) {
            alert(err?.response?.data?.detail || err?.message || 'Nu am putut reveni la panoul Super Admin');
        }
    };

    return (
        <div className="grain dash-shell">
            <header className="dash-topbar">
                <button type="button" className="dash-burger" onClick={() => setMobileOpen(true)} aria-label="Deschide meniul" data-testid="dash-menu-toggle">
                    <Menu size={22} />
                </button>
                <Logo size="sm" to="/r" />
            </header>

            {mobileOpen && <div className="dash-backdrop" onClick={closeMobile} data-testid="dash-backdrop" />}

            <aside className={`dash-sidebar${mobileOpen ? ' dash-sidebar--open' : ''}`}>
                {/* Header — logo + close button (mobile only) */}
                <div className="dash-sidebar__head">
                    <Logo size="md" to="/r" />
                    <button type="button" className="dash-sidebar__close" onClick={closeMobile} aria-label="Închide meniul" data-testid="dash-menu-close">
                        <X size={20} />
                    </button>
                </div>

                {/* User card — compact: name + role chip. Restaurant + cod login below as small footer. */}
                <div className="dash-user-card" data-testid="user-session-info">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="dash-user-avatar" aria-hidden="true">
                            {(user?.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="dash-user-name" data-testid="user-name">{user?.name || '—'}</div>
                            <div className="dash-user-role" data-testid="user-role">{roleLabel(user?.role)}</div>
                        </div>
                    </div>
                    {(restaurant?.name || restaurant?.staff_login_id) && (
                        <div className="dash-user-card__meta">
                            <LocationSwitcher />
                            {restaurant?.staff_login_id && (
                                <div className="dash-user-code" data-testid="restaurant-login-code">
                                    <span className="dash-user-code__label">Cod login</span>
                                    <span className="dash-user-code__value">{restaurant.staff_login_id}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Main navigation — operational features used daily */}
                <nav className="dash-nav" aria-label="Navigare principală">
                    {links.map((l) => {
                        const subLocked = l.lockReason === 'subscription';
                        const tooltip = subLocked
                            ? 'Abonament inactiv — activează un plan pentru a debloca'
                            : (l.locked ? 'Funcție Pro — fă upgrade pentru a o debloca' : undefined);
                        return (
                            <NavLink
                                key={l.to}
                                to={subLocked ? '/r/settings?paywall=1#plan' : l.to}
                                end={subLocked ? false : l.end}
                                onClick={(e) => {
                                    if (subLocked) {
                                        try {
                                            sessionStorage.setItem('tf_paywall_msg', 'Abonament inactiv pentru această locație. Activează un plan pentru a debloca aplicația.');
                                        } catch {}
                                    }
                                    closeMobile();
                                }}
                                className={`sidebar-link${l.locked ? ' sidebar-link--locked' : ''}`}
                                title={tooltip}
                                data-testid={`nav-${l.to.replace('/r/', '').replace('/r', 'overview')}`}
                            >
                                <l.icon size={18} /> <span style={{ flex: 1 }}>{l.label}</span>
                                {l.locked && <Lock size={14} style={{ opacity: 0.7 }} data-testid={`nav-lock-${l.feature || (subLocked ? 'subscription' : '')}`} />}
                            </NavLink>
                        );
                    })}
                </nav>

                {/* Account section — separated visually from main nav */}
                <div className="dash-sidebar__footer">
                    {user?.role === 'restaurant_admin' && (
                        <NavLink
                            to={locationsLink.to}
                            onClick={closeMobile}
                            className="sidebar-link"
                            data-testid="nav-locations"
                        >
                            <locationsLink.icon size={18} /> <span style={{ flex: 1 }}>{locationsLink.label}</span>
                        </NavLink>
                    )}
                    {showAccount && (
                        <NavLink
                            to={accountLink.to}
                            onClick={closeMobile}
                            className="sidebar-link"
                            data-testid="nav-settings"
                        >
                            <accountLink.icon size={18} /> <span style={{ flex: 1 }}>{accountLink.label}</span>
                        </NavLink>
                    )}
                    <button onClick={handleLogout} className="dash-logout-btn" data-testid="logout-btn">
                        <LogOut size={18} /> Logout
                    </button>
                </div>
            </aside>

            <main className="dash-main">
                {impersonating && (
                    <div className="panel" style={{
                        marginBottom: '1rem', padding: '0.75rem 1rem',
                        background: 'rgba(255, 46, 146, 0.08)', border: '1px solid rgba(255, 46, 146, 0.35)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap',
                    }} data-testid="impersonation-banner">
                        <div style={{ fontSize: '0.9rem' }}>
                            <strong style={{ color: 'var(--neon-magenta)' }}>Mod Super Admin</strong>
                            <span style={{ color: 'var(--text-muted)' }}> — gestionezi </span>
                            <strong>{restaurant?.name}</strong>
                            {restaurant?.staff_login_id && (
                                <span style={{ marginLeft: 8, fontFamily: 'monospace', letterSpacing: '0.1em', color: 'var(--neon-cyan)', textShadow: '0 0 10px rgba(56, 189, 248, 0.4)' }}>
                                    · {restaurant.staff_login_id}
                                </span>
                            )}
                        </div>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={backToSuperAdmin} data-testid="exit-impersonation">
                            ← Înapoi la panoul Super Admin
                        </button>
                    </div>
                )}
                <Outlet />
                <WaiterAlertPanel restaurantId={user?.restaurant_id} userRole={isPlatformPanelUser(user?.role) ? 'restaurant_admin' : user?.role} />
                <SoundConsentBanner />
            </main>
        </div>
    );
}
